import React from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Text,
  Flex,
  Box,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useLocation } from 'react-use';

import Image from '@repo/ui-components/tailwind/Image';
import SimpleSlider from '@repo/ui-components/dept/molecules/SimpleSlider';
import { generateSectionId } from '@repo/ui-components/dept/utils/generateSectionId';
import Link from '@repo/ui-components/tailwind/Link';
import CTAButton from '@repo/ui-components/tailwind/CTAButton';
import { shrinkProductImage, isShopInPathname } from '@repo/utils';
import { SanityBackgroundImage } from '../../../atoms';

function ProductBox(product) {
  return (
    <Box
      as={Link}
      className="flex flex-col"
      key={product?._id}
      link={`/product/${product?.store?.slug.current}`}
      w={{ base: '13rem', md: '15rem' }}
    >
      <Image src={shrinkProductImage(product?.store?.previewImageUrl, 360)} />
      <Text>{product?.store?.title}</Text>
    </Box>
  );
}

ProductBox.propTypes = {
  product: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    store: PropTypes.shape({
      previewImageUrl: PropTypes.string,
      slug: PropTypes.shape({
        current: PropTypes.string,
      }),
      title: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

function ProductCard({ title, image, url, ...props }) {
  const location = useLocation();

  const getLinkUrl = () => {
    if (url) {
      if (isShopInPathname(location?.pathname)) {
        return `${url}?collection-location=${location.pathname}`;
      }
      return url;
    }
    return '';
  };

  return (
    <Box
      as={Link}
      className="hover:no-underline hover:border-cta"
      link={getLinkUrl()}
      bg="white"
      p={6}
      borderRadius="lg"
      boxShadow="0px 4px 56px 0px rgba(5, 37, 77, 0.15)"
      w="full"
      aspectRatio={1}
      border="4px solid white"
      {...props}
    >
      <Image
        alt={title}
        className="mb-2 [&>img]:object-contain"
        ratio={4 / 3}
        src={image}
        width="100%"
      />
      <Text textStyle="caption" fontWeight="semibold">
        {title}
      </Text>
    </Box>
  );
}

ProductCard.propTypes = {
  title: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
};

const Lifestyle = ({ block }) => {
  if (!block) return null;

  const { title, description, bgImg, cta, featuredProducts } = block;

  return (
    <SanityBackgroundImage
      {...generateSectionId(block)}
      my={{ base: 8, lg: 28 }}
      pos="relative"
      zIndex={0}
      image={bgImg || block.image}
      gradientGenerator={url =>
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useBreakpointValue({
          lg: `linear-gradient(90deg, rgba(0, 0, 0, 0.60) 1%, rgba(0, 0, 0, 0) 70%), url(${url}), lightgray -0.302px -61.503px / 112.123% 186.894% no-repeat`,
          xl: `linear-gradient(90deg, rgba(0, 0, 0, 0.60) 1%, rgba(0, 0, 0, 0.30) 30%, rgba(0, 0, 0, 0) 54%), url(${url}), lightgray -0.302px -61.503px / 112.123% 186.894% no-repeat`,
          '2xl': `linear-gradient(90deg, rgba(0, 0, 0, 0.60) 1%, rgba(0, 0, 0, 0.40) 23%, rgba(0, 0, 0, 0) 45%), url(${url}), lightgray -0.302px -61.503px / 112.123% 186.894% no-repeat`,
        })
      }
      breakpointOverrides={{
        base: 'none',
        sm: 'none',
        md: 'none',
      }}
    >
      <Container h={{ base: 'auto', lg: '36rem' }} zIndex={2}>
        <Flex
          pos="relative"
          justifyContent="start"
          alignItems="center"
          h="full"
          direction={{ base: 'column', lg: 'row' }}
        >
          <Flex
            direction="column"
            justify="center"
            w={{ base: 'full', lg: 'xl' }}
          >
            <Text
              as="h2"
              textStyle="h1"
              color={{ base: 'typography.base', lg: 'typography.lighter' }}
              mb={6}
              fontWeight="black"
            >
              {title}
            </Text>
            <Text
              textStyle="bodyLarge"
              color={{ base: 'typography.base', lg: 'typography.lighter' }}
              mb={10}
              fontWeight="bold"
            >
              {description}
            </Text>
            <Box pos="relative">
              {cta?.text && (
                <CTAButton
                  className="relative bottom-6 top-1 lg:static lg:bottom-auto lg:right-auto z-[1] h-12"
                  cta={cta}
                >
                  {cta.text}
                </CTAButton>
              )}
              {(bgImg || block.image) && (
                <SanityBackgroundImage
                  display={{ base: 'block', lg: 'none' }}
                  image={bgImg ?? block.image}
                  aspectRatio={1}
                  maxImageWidth={1600}
                />
              )}
            </Box>
          </Flex>

          <Flex
            display={{ base: 'none', lg: 'flex' }}
            pos={{ base: 'initial', lg: 'absolute' }}
            top={{ base: 'initial', lg: '50%' }}
            right={{ base: 'initial', lg: '0' }}
            transform={{ base: 'none', lg: 'translateY(-50%)' }}
            direction={{ base: 'row', lg: 'column' }}
            justifyContent="start"
            alignItems="center"
            gap={{ base: 0, lg: 4 }}
            w={{ base: '100%', lg: '14rem' }}
            overflowX={{ base: 'scroll', lg: 'initial' }}
            p={{ base: 4, lg: 0 }}
          >
            {featuredProducts?.map(product =>
              product ? (
                <ProductCard
                  key={product._key}
                  minW="14rem"
                  title={product.store.title}
                  image={shrinkProductImage(product.store.previewImageUrl, 360)}
                  url={`/product/${product.store.slug.current}`}
                  boxShadow="0px 4px 56px 0px rgba(5, 37, 77, 0.15)"
                />
              ) : null
            )}
          </Flex>
        </Flex>
        <SimpleSlider
          display={{ lg: 'none' }}
          mt={4}
          items={featuredProducts || []}
          sliderItem={ProductBox}
        />
      </Container>
    </SanityBackgroundImage>
  );
};

Lifestyle.propTypes = {
  block: PropTypes.shape({
    _key: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    bgImg: PropTypes.shape({
      alt: PropTypes.string,
      asset: PropTypes.shape({
        url: PropTypes.string,
      }),
      hotspot: PropTypes.shape({
        x: PropTypes.number,
        y: PropTypes.number,
      }),
    }),
    image: PropTypes.shape({
      alt: PropTypes.string,
      asset: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
    cta: PropTypes.shape({
      text: PropTypes.string,
      link: PropTypes.shape({
        linkType: PropTypes.string,
        href: PropTypes.string,
        internalLink: PropTypes.shape({}),
      }),
    }),
    featuredProducts: PropTypes.arrayOf(
      PropTypes.shape({
        _id: PropTypes.string,
        store: PropTypes.shape({
          previewImageUrl: PropTypes.string,
          title: PropTypes.string,
          slug: PropTypes.shape({
            current: PropTypes.string,
          }),
        }),
      })
    ),
  }).isRequired,
};

export default Lifestyle;
